import { Location } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { promiseWithCatch, unsubscribeAll } from '../../../common/app.helpers';
import ProjectLinks from '../../../common/project.links';
import { FieldAttributeConstructorService } from '../../../forms/fields/field-attributes-constructor.service';
import { FormsAttributeService } from '../../../forms/forms-attribute.service';
import { FormTypes } from '../../../forms/forms.enum';
import { FormConstructorContent } from '../../../interfaces/form-and-field.interface';
import { PostWithSocialContext } from '../../../interfaces/posts.interface';
import { Post, DefaultService as RestApiService, UpdatePostRequest } from '../../../swagger-api';
import { postMetadata } from '../post.metadata';

@Injectable({ providedIn: 'root' })
export class UpdatePostService implements OnDestroy {
  private postChanges$: Subject<Post> = new Subject();
  private postFormChanges$: ReplaySubject<FormConstructorContent> = new ReplaySubject();

  private subscriptions$: Subscription[] = [];

  private location = inject(Location);
  private formsAttributeService = inject(FormsAttributeService);
  private fieldsService = inject(FieldAttributeConstructorService);
  private apiService = inject(RestApiService);
  private router = inject(Router);

  public ngOnDestroy(): void {
    this.postChanges$.complete();
    this.postFormChanges$.complete();
    unsubscribeAll(this.subscriptions$);
  }

  public get postChanges(): Observable<Post> {
    return this.postChanges$.asObservable();
  }

  /**
   * @description Fetches the post info and calls loadInitialPostComments() method.
   */
  public getPostInfo(postId: string): void {
    this.subscriptions$.push(
      this.apiService.restSocialPostsGet(postId).subscribe({
        next: (post: Post) => {
          this.postChanges$.next(post);
        },
        error: () => {
          promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_DEFAULT));
        },
      }),
    );
  }

  public get updatePostFormSubject(): Observable<FormConstructorContent> {
    return this.postFormChanges$.asObservable();
  }

  public fetchAttributeMetadata(data?: PostWithSocialContext): void {
    const mockedAttrs: FormConstructorContent = {
      formAttribute: this.formsAttributeService.getFormAttribute(FormTypes.POST),
      fieldAttributes: this.fieldsService.convertAttributesMetadata(postMetadata, FormTypes.POST),
      data: data ? data : {},
    };

    this.postFormChanges$.next(mockedAttrs);
  }

  public updatePost(body: UpdatePostRequest, postId: string): void {
    this.apiService
      .restSocialPostsPut(body, postId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        promiseWithCatch(this.router.navigate([`${ProjectLinks.LINK_POSTS_VIEW}/${postId}`]));
      });
  }

  public cancel(): void {
    this.location.back();
  }
}
